/**
 * GlobalView — Train Icons
 *
 * Canvas-based train icon rendering with category-based coloring.
 * Icons are cached for performance, same pattern as aircraftIcons.ts.
 */

// ─── Category Colors ─────────────────────────────────────────────

const CATEGORY_COLORS: Record<string, string> = {
  ICE: '#e3000f',
  TGV: '#e3000f',
  IC:  '#2d5ea2',
  EC:  '#2d5ea2',
  IR:  '#4a9a2f',
  RE:  '#f39200',
  S:   '#7b2d8e',
  R:   '#888888',
  RB:  '#888888',
};

const DEFAULT_COLOR = '#888888';
const ICON_SIZE = 24;

// ─── Cache ───────────────────────────────────────────────────────

const canvasCache = new Map<string, HTMLCanvasElement>();

// ─── Category Label ──────────────────────────────────────────────

export function getTrainCategoryLabel(category: string): string {
  const labels: Record<string, string> = {
    ICE: 'InterCity Express',
    TGV: 'TGV',
    IC: 'InterCity',
    EC: 'EuroCity',
    IR: 'InterRegio',
    RE: 'RegionalExpress',
    S: 'S-Bahn',
    R: 'Regional',
    RB: 'Regionalbahn',
  };
  return labels[category] ?? category;
}

export function getTrainCategoryColor(category: string): string {
  return CATEGORY_COLORS[category] ?? DEFAULT_COLOR;
}

// ─── Icon Rendering ──────────────────────────────────────────────

/**
 * Renders a top-down train icon onto a Canvas.
 * The icon is a simple rounded rectangle with a dark outline
 * and a colored fill based on category.
 */
export function getTrainIconCanvas(category: string): HTMLCanvasElement {
  const cacheKey = category;
  const cached = canvasCache.get(cacheKey);
  if (cached) return cached;

  const size = ICON_SIZE * 2; // 2x for retina
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;

  const ctx = canvas.getContext('2d')!;
  const color = getTrainCategoryColor(category);

  const cx = size / 2;
  const cy = size / 2;

  // Outer circle (dark border)
  ctx.beginPath();
  ctx.arc(cx, cy, size * 0.4, 0, Math.PI * 2);
  ctx.fillStyle = '#1a1a2e';
  ctx.fill();

  // Inner circle (category color)
  ctx.beginPath();
  ctx.arc(cx, cy, size * 0.32, 0, Math.PI * 2);
  ctx.fillStyle = color;
  ctx.fill();

  // Train icon shape (simplified top-down rectangle)
  ctx.fillStyle = '#ffffff';
  ctx.strokeStyle = '#ffffff';
  ctx.lineWidth = 1.5;
  ctx.lineCap = 'round';

  // Body
  const bw = size * 0.14;
  const bh = size * 0.26;
  const bx = cx - bw / 2;
  const by = cy - bh / 2;
  roundedRect(ctx, bx, by, bw, bh, 3);
  ctx.fill();

  // Front indicator (direction dot)
  ctx.beginPath();
  ctx.arc(cx, cy - bh / 2 + 3, 2, 0, Math.PI * 2);
  ctx.fillStyle = '#1a1a2e';
  ctx.fill();

  canvasCache.set(cacheKey, canvas);
  return canvas;
}

/** Draw a rounded rectangle on ctx */
function roundedRect(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  width: number,
  height: number,
  radius: number,
) {
  ctx.beginPath();
  ctx.moveTo(x + radius, y);
  ctx.lineTo(x + width - radius, y);
  ctx.quadraticCurveTo(x + width, y, x + width, y + radius);
  ctx.lineTo(x + width, y + height - radius);
  ctx.quadraticCurveTo(x + width, y + height, x + width - radius, y + height);
  ctx.lineTo(x + radius, y + height);
  ctx.quadraticCurveTo(x, y + height, x, y + height - radius);
  ctx.lineTo(x, y + radius);
  ctx.quadraticCurveTo(x, y, x + radius, y);
  ctx.closePath();
}

/** Get icon as data URI for Cesium Billboards */
export function getTrainIconDataUri(category: string): string {
  const canvas = getTrainIconCanvas(category);
  return canvas.toDataURL('image/png');
}
